import { auth } from '@/lib/firebase/app'
import { ensureRemoteConfig, getServer } from '@/lib/firebase/remote-config'

export class VoypApiError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'VoypApiError'
    this.status = status
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
}

async function authHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser
  if (!user) throw new VoypApiError('Not signed in', 401)
  const token = await user.getIdToken(false)
  return { Authorization: `Bearer ${token}` }
}

/**
 * Single choke point for all API calls.
 * The server frequently returns HTTP 200 with an `{error}` body — normalize
 * both transport and in-body errors into VoypApiError.
 */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  await ensureRemoteConfig()
  const headers: Record<string, string> = await authHeader()
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'

  const res = await fetch(getServer() + path, {
    method: options.method ?? 'GET',
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  })

  if (!res.ok) {
    let message = `Request failed (${res.status})`
    try {
      const body = await res.json()
      if (body?.error) message = String(body.error)
    } catch {
      // Non-JSON error body; keep the status message.
    }
    throw new VoypApiError(message, res.status)
  }

  const data = (await res.json()) as T & { error?: string }
  if (data && typeof data === 'object' && 'error' in data && data.error) {
    throw new VoypApiError(String(data.error), res.status)
  }
  return data
}

/** Fetch a binary response (e.g. TTS audio) with auth. */
export async function apiFetchBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  await ensureRemoteConfig()
  const headers: Record<string, string> = await authHeader()
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'

  const res = await fetch(getServer() + path, {
    method: options.method ?? 'GET',
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  })
  if (!res.ok) throw new VoypApiError(`Request failed (${res.status})`, res.status)
  return res.blob()
}

/** Text response with auth (e.g. RSS feed XML). */
export async function apiFetchText(path: string): Promise<string> {
  await ensureRemoteConfig()
  const headers = await authHeader()
  const res = await fetch(getServer() + path, { headers })
  if (!res.ok) throw new VoypApiError(`Request failed (${res.status})`, res.status)
  return res.text()
}
